import { permissionError } from '../actions/error'
import { SYSTEM_REDUCER_KEYS } from '../reducers/systemReducers'
import selectors from '../selectors'

const FLAT_ARRAY_KEYS = ['cards', 'notes', 'places', 'characters']

/**
 * @typedef {import('../index').PlottrFile} PlottrFile
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').PlottrFileEntity} PlottrFileEntity
 * @typedef {import('../index').PlottrStore} PlottrStore
 * @typedef {import('../index').PltrAction} PltrAction
 * @typedef {import('../index').File} File
 * @typedef {import('../index').PlottrFileUi} PlottrFileUi
 * @typedef {import('../index').FeatureFlags} FeatureFlags
 * @typedef {import('../index').PlottrFileSeries} PlottrFileSeries
 * @typedef {import('../index').Books} Books
 * @typedef {import('../index').PlottrFileBeats} PlottrFileBeats
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').FileCategories} FileCategories
 * @typedef {import('../index').Character} Character
 * @typedef {import('../index').LegacyCustomAttributes} LegacyCustomAttributes
 * @typedef {import('../index').Attributes} Attributes
 * @typedef {import('../index').Line} Line
 * @typedef {import('../index').Note} Note
 * @typedef {import('../index').Place} Place
 * @typedef {import('../index').Tag} Tag
 * @typedef {import('../index').HierarchyLevels} HierarchyLevels
 * @typedef {import('../index').PlottrFileImages} PlottrFileImages
 * @typedef {import('../index').PlottrFileAudioClips} PlottrFileAudioClips
 * @typedef {import('../index').PlottrFileFamilyTrees} PlottrFileFamilyTrees
 */

/**
 * @param {keyof PlottrFile} key
 * @return {boolean}
 */
export const isFlatArrayKey = (key) => {
  return FLAT_ARRAY_KEYS.indexOf(key) !== -1
}

/**
 * @typedef {'cards' | 'notes' | 'places' | 'characters'} FlatArrayKey
 */

/**
 * @param {keyof PlottrFile} key
 * @return {FlatArrayKey | Null}
 */
export const flatArrayKeyOrNull = (key) => {
  if (key === 'cards' || key === 'notes' || key === 'places' || key === 'characters') {
    return key
  } else {
    return null
  }
}

/**
 * @param {keyof PlottrFile} key
 * @return {'file'| 'ui'| 'featureFlags'| 'series'| 'books'| 'beats'| 'categories'| 'customAttributes'| 'attributes'| 'lines'| 'tags'| 'hierarchyLevels'| 'images'| 'audioClips'| 'familyTrees'| Null}
 */
export const nonFlatArrayKeyOrNull = (key) => {
  if (key !== 'cards' && key !== 'notes' && key !== 'places' && key !== 'characters') {
    return key
  } else {
    return null
  }
}

/**
 * @param {Array<String>} keys
 * @return {Array<keyof PlottrFile>}
 */
const keepOnlyFileStateKeys = (keys) => {
  return keys.reduce((acc, next) => {
    if (
      next === 'file' ||
      next === 'ui' ||
      next === 'featureFlags' ||
      next === 'series' ||
      next === 'books' ||
      next === 'beats' ||
      next === 'cards' ||
      next === 'categories' ||
      next === 'characters' ||
      next === 'customAttributes' ||
      next === 'attributes' ||
      next === 'lines' ||
      next === 'notes' ||
      next === 'places' ||
      next === 'tags' ||
      next === 'hierarchyLevels' ||
      next === 'images' ||
      next === 'audioClips' ||
      next === 'familyTrees'
    ) {
      return [...acc, next]
    } else {
      return acc
    }
  }, [])
}

const ADDED = 'ADDED'
const UPDATED = 'UPDATED'
const DELETED = 'DELETED'

/**
 * @typedef {function(keyof PlottrFile, String, PlottrFileEntity, String, number | Null): Promise<Void>} PatchFunction
 * @typedef {function(keyof PlottrFile, String, String, number): Promise<Void>} DeleteSingleFunction
 * @typedef {function(keyof PlottrFile, PlottrFile[keyof PlottrFile]): any} WithDataFunction
 */

/**
 * Synchronise with Firebase.  We know to sync if there's a difference
 * between the previous value and the current value.  Synchronise
 * Redux key by key in a subset of keys that are appropriate for
 * Firebase.  Produce true if we actually synchronised.
 *
 * @param {function(any): PlottrState} selectState
 */
export const sync = (selectState) => {
  const {
    fileIdSelector,
    clientIdSelector,
    isCloudFileSelector,
    isOfflineSelector,
    isResumingSelector,
    selectedFileIdSelector,
    filePermissionSelector,
    fullFileStateSelector,
  } = selectors(selectState)
  /**
   * @param {PreviousStateIndex} previous
   * @param {PlottrState} present
   * @param {PatchFunction} patch
   * @param {DeleteSingleFunction} deleteSingle
   * @param {WithDataFunction} withData
   * @param {PlottrStore} store
   * @param {PltrAction} action
   * @param {Array<ChangePath>} updatedPaths
   */
  return (previous, present, patch, deleteSingle, withData, store, action, updatedPaths) => {
    const isCloudFile = isCloudFileSelector(present)
    const isOffline = isOfflineSelector(present)
    const isResuming = isResumingSelector(present)
    const fileId = fileIdSelector(present)
    const clientId = clientIdSelector(present)
    const selectedFileId = selectedFileIdSelector(present)
    const userPermission = filePermissionSelector(present)
    const notPermittedToChangeFile = userPermission !== 'owner' && userPermission !== 'collaborator'
    if (
      // We might not be allowed to change the file.
      notPermittedToChangeFile ||
      // We might've just loaded the file.
      action.type === 'FILE_LOADED' ||
      // Some actions that aren't appropriate
      action.type === 'PERMISSION_ERROR' ||
      action.type === 'CLEAR_ERROR' ||
      // On the initial read from Firebase, we mark that we're patching
      // the store until it's ready for use by the user.
      ('patching' in action && action.patching) ||
      // We might not have an appropriate file loaded.
      !isCloudFile ||
      isOffline ||
      isResuming ||
      !fileId ||
      !clientId ||
      selectedFileId !== fileId
    ) {
      return false
    }

    // It's possible that nothing that we can compare to happened yet.
    if (!previous) return false

    const state = fullFileStateSelector(present)
    for (let i = 0; i < updatedPaths.length; ++i) {
      const { path, change, index } = updatedPaths[i]
      const key = path[0]
      const payload = withData(key, state[key])
      if (isFlatArrayKey(key)) {
        /**
         * @type {PlottrFileEntity | Null}
         */
        const entity =
          index !== null && Array.isArray(state[key]) ? state[key][index] || null : null
        if (change !== DELETED && entity && 'id' in entity && typeof entity.id === 'number') {
          patch(key, fileId, entity, clientId, entity.id).catch((error) => {
            if (error.code === 'permission-denied') {
              store.dispatch(permissionError(key, action, error.code))
            }
          })
        } else if (change === DELETED) {
          const id = path[1]
          if (typeof id === 'number') {
            deleteSingle(key, fileId, clientId, id).catch((error) => {
              if (error.code === 'permission-denied') {
                store.dispatch(permissionError(key, action, error.code))
              }
            })
          }
        }
      } else {
        patch(key, fileId, payload, clientId, null).catch((error) => {
          if (error.code === 'permission-denied') {
            store.dispatch(permissionError(key, action, error.code))
          }
        })
      }
    }

    return true
  }
}

/**
 * @typedef PreviousStateIndex
 * @property {File} file
 * @property {PlottrFileUi} ui
 * @property {FeatureFlags} featureFlags
 * @property {PlottrFileSeries} series
 * @property {Books} books
 * @property {PlottrFileBeats} beats
 * @property {Map<number, Card>} cards
 * @property {FileCategories} categories
 * @property {Map<number, Character>} characters
 * @property {LegacyCustomAttributes} customAttributes
 * @property {Attributes} attributes
 * @property {Array<Line>} lines
 * @property {Map<number, Note>} notes
 * @property {Map<number, Place>} places
 * @property {Array<Tag>} tags
 * @property {HierarchyLevels} hierarchyLevels
 * @property {PlottrFileImages} images
 * @property {PlottrFileAudioClips} audioClips
 * @property {PlottrFileFamilyTrees} familyTrees
 */

/**
 * @typedef ChangePath
 * @property {[keyof PlottrFile] | [keyof PlottrFile, number]} path
 * @property {'DELETED' | 'ADDED' | 'UPDATED'} change
 * @property {number | Null} index
 */

/**
 * @param {PreviousStateIndex} previous
 * @param {PlottrState} state
 * @param {typeof import('../selectors/allSelectors').allSelectors} wiredSelectors
 * @return {Array<ChangePath>}
 */
export const computeNewPaths = (previous, state, wiredSelectors) => {
  const { fullFileStateSelector, filePermissionSelector } = wiredSelectors

  const fileState = fullFileStateSelector(state)
  const userPermission = filePermissionSelector(state)

  const resultPaths = []
  const fullStateKeys = keepOnlyFileStateKeys(Object.keys(fileState))
  for (let i = 0; i < fullStateKeys.length; ++i) {
    const key = fullStateKeys[i]
    const isFileAndHasntGotPermission = key === 'file' && userPermission !== 'owner'
    if (SYSTEM_REDUCER_KEYS.indexOf(key) === -1 && !isFileAndHasntGotPermission) {
      if (isFlatArrayKey(key) && Array.isArray(fileState[key])) {
        const here = new Set()
        const arrayEntityMap = previous[key]
        if (arrayEntityMap instanceof Map) {
          for (let index = 0; index < fileState[key].length; ++index) {
            const entity = fileState[key][index]
            here.add(entity.id)
            const previousEntity = arrayEntityMap.get(entity.id)
            if (!previousEntity) {
              /**
               * @type {ChangePath}
               */
              const path = { path: [key, entity.id], change: ADDED, index }
              resultPaths.push(path)
            } else if (!Object.is(entity, previousEntity)) {
              /**
               * @type {ChangePath}
               */
              const path = { path: [key, entity.id], change: UPDATED, index }
              resultPaths.push(path)
            }
          }
          for (const previousId of arrayEntityMap.keys()) {
            if (!here.has(previousId)) {
              /**
               * @type {ChangePath}
               */
              const path = { path: [key, previousId], change: DELETED, index: null }
              resultPaths.push(path)
            }
          }
        }
      } else {
        if (fileState[key] !== previous[key]) {
          /**
           * @type {ChangePath}
           */
          const path = { path: [key], change: UPDATED, index: null }
          resultPaths.push(path)
        }
      }
    }
  }
  return resultPaths
}

/**
 * @param {PlottrFile} state
 * @return {PreviousStateIndex}
 */
export function keyFlatArraysById(state) {
  /**
   * @template T
   * @param {Array<T>} array
   * @return {Map<number, T>}
   */
  const keyedById = (array) => {
    const byId = new Map()
    for (const element of array) {
      if (
        typeof element === 'object' &&
        element &&
        'id' in element &&
        typeof element.id === 'number'
      ) {
        byId.set(element.id, element)
      }
    }
    return byId
  }

  return {
    file: state.file,
    ui: state.ui,
    featureFlags: state.featureFlags,
    series: state.series,
    books: state.books,
    beats: state.beats,
    cards: keyedById(state.cards),
    categories: state.categories,
    characters: keyedById(state.characters),
    customAttributes: state.customAttributes,
    attributes: state.attributes,
    lines: state.lines,
    notes: keyedById(state.notes),
    places: keyedById(state.places),
    tags: state.tags,
    hierarchyLevels: state.hierarchyLevels,
    images: state.images,
    audioClips: state.audioClips,
    familyTrees: state.familyTrees,
  }
}

/**
 * @param {FlatArrayKey} key
 * @param {number} id
 * @param {PreviousStateIndex} previous
 * @param {PlottrFileEntity} current
 */
const setPreviousInMap = (key, id, previous, current) => {
  /**
   * @template T
   * @param {Map<FlatArrayKey, T>} map
   * @param {FlatArrayKey} mapKey
   * @param {T} value
   */
  const templatisedSet = (map, mapKey, value) => {
    map.set(mapKey, value)
  }
  templatisedSet(
    // FIXME(ed): I'm not sure how to solve this type conundrum.  TS
    // isn't inferring that the specific map type pulled from the
    // record must line up in this case.
    //
    // @ts-ignore
    previous[key],
    id,
    current
  )
}

/**
 * @param {PreviousStateIndex} previous
 * @param {Array<ChangePath>} updatedPaths
 * @param {PlottrFile} state
 */
export function updatePrevious(previous, updatedPaths, state) {
  for (const updatedPath of updatedPaths) {
    const { path, change, index } = updatedPath
    const [key, id] = path
    const flatArrayKey = flatArrayKeyOrNull(key)
    const nonFlatArrayKey = nonFlatArrayKeyOrNull(key)
    if (flatArrayKey) {
      if (typeof id === 'number') {
        const entity = state[flatArrayKey]
        if (change === UPDATED && Array.isArray(entity) && index !== null) {
          const currentEntity = entity[index]
          if (currentEntity) {
            setPreviousInMap(flatArrayKey, id, previous, currentEntity)
          }
        } else if (change === ADDED && index !== null) {
          const currentEntity = entity[index]
          if (currentEntity) {
            setPreviousInMap(flatArrayKey, id, previous, currentEntity)
          }
        } else if (change === DELETED) {
          previous[flatArrayKey].delete(id)
        } else {
          // TODO(ed): I'm not sure what should happen here.  I think
          // in this case, we computed an invalid path.  We can't
          // update anything, but this function is also presently
          // infallible.  Should we check it from the calling site, or
          // assume that it's correct here and this branch is
          // impossible(?)
        }
      }
    } else if (nonFlatArrayKey) {
      // FIXME(ed): I'm not sure how to convince the TS compiler that
      // the types must line up since the keys are the same and we're
      // only talking about non-array entities(!?)
      //
      // @ts-ignore
      previous[nonFlatArrayKey] = state[nonFlatArrayKey]
    }
  }
}

/**
 * @param {function(any): PlottrState} selectState
 * @return {function(PatchFunction, DeleteSingleFunction, WithDataFunction): (function(PlottrStore): (function(function(PltrAction): PlottrState): function(PltrAction): PlottrState))}
 */
const externalSync = (selectState) => {
  // Note: we mutate previous in-place each time there's a change.
  /**
   * @type {PreviousStateIndex | Null}
   */
  let previous = null
  const wiredSync = sync(selectState)
  const wiredSelectors = selectors(selectState)
  const { fullFileStateSelector } = selectors(selectState)
  return (patch, deleteSingle, withData) => (store) => (next) => (action) => {
    if (previous === null) {
      previous = keyFlatArraysById(fullFileStateSelector(store.getState()))
    } else {
      const fileChanged = previous?.file?.id !== fullFileStateSelector(store.getState())?.file?.id
      if (fileChanged) {
        previous = keyFlatArraysById(fullFileStateSelector(store.getState()))
      }
    }

    const result = next(action)

    const present = store.getState()
    const updatedPaths = computeNewPaths(previous, present, wiredSelectors)
    // If we're patching in data from remote, then we need to update
    // our record of the previous state to equal what the server
    // said so that we don't echo it back in an infinite loop.
    if ('patching' in action && action.patching) {
      updatePrevious(previous, updatedPaths, fullFileStateSelector(present))
    } else {
      const synchronised = wiredSync(
        previous,
        present,
        patch,
        deleteSingle,
        withData,
        store,
        action,
        updatedPaths
      )
      if (synchronised) {
        updatePrevious(previous, updatedPaths, fullFileStateSelector(present))
      }
    }

    return result
  }
}

export default externalSync

export const externalSyncWithoutHistory = externalSync
